<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomButton,
  AtomRadio,
  AtomText,
  SatFormControl,
  WoLocation,
} from "@chatfood/design-system";
import { computed, onMounted, ref } from "vue";
import { BasketEventName } from "../basket-event-name";
import { BasketPax } from "../basket-pax";
import { SchedulePicker } from "../schedule-picker";
import type { IPickupOrderingDetailsEmits } from "./emits";
import { useBugReporter, useDirectBackend } from "#imports";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useLocale } from "~/composables/use-locale";
import { useMinimumLeadTime } from "~/composables/use-minimum-lead-time";
import { useNavigation } from "~/composables/use-navigation";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import type { IDomainOrderingModeSettings } from "~/domain/ordering-mode-settings";
import type { IDomainOutletState } from "~/domain/outlet-state";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useItemSchedulesStore } from "~/stores/itemSchedules";

const emits = defineEmits<IPickupOrderingDetailsEmits>();

const css = bemBuilder("pickup-ordering-details");

const { t } = useLocale();
const { push, resolvePath } = useNavigation();
const { outletStateMap } = useDomainLabel();
const { outletRepo } = useDirectBackend();
const { report } = useBugReporter();

const basket = useBasketStore();
const outlet = useOutletStore();
const business = useBusinessStore();
const itemSchedulesStore = useItemSchedulesStore();

type IOrderType = "asap" | "scheduled";

const PICKUP = "pickup";

const freshOrderingModeSettings = ref<IDomainOrderingModeSettings>();

const isActiveOrderingMode = computed(() => {
  return outlet.activeOrderingMode === PICKUP;
});

const orderingModeSettings = computed(() => {
  return freshOrderingModeSettings.value ?? outlet.orderingModes.pickup;
});

const loadOrderingModeSettings = async () => {
  if (isActiveOrderingMode.value) return;

  try {
    const [settings] = await outletRepo.getSettings({
      outletId: outlet.id,
      salesChannel: basket.salesChannel,
      orderingMode: [PICKUP],
    });

    if (!settings) return;

    freshOrderingModeSettings.value = {
      ...settings,
      outletState: settings.operationalState,
    };

    if (!settings.acceptsAsapOrders || settings.operationalState !== "open") {
      orderType.value = "scheduled";
    }
  } catch (e) {
    report(e);
  }
};

onMounted(loadOrderingModeSettings);

const scheduledTime = ref<IDomainOrderScheduledTime | undefined>(
  basket.orderingMode === PICKUP ? basket.scheduledTime : undefined,
);

const scheduleErrorMessage = ref<string>();

const orderType = ref<IOrderType>(
  orderingModeSettings.value.acceptsAsapOrders &&
    orderingModeSettings.value.outletState === "open" &&
    !scheduledTime.value
    ? "asap"
    : "scheduled",
);
const isSettingMode = ref(false);
const selectedOutletId = ref<string | undefined>(basket.outletId);

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: "open",
  closed: "close",
  busy: "busy",
  pre_order_only: "soon",
};

const cardDescription = computed(() => {
  if (
    orderingModeSettings.value.outletState === "closed" ||
    !orderingModeSettings.value.preparationMinutes
  ) {
    return "";
  }

  return t("components.pickup_ordering_details.outlet_card_hint", {
    prepStart: orderingModeSettings.value.preparationMinutes,
    prepEnd:
      orderingModeSettings.value.preparationMinutes + ETA_PREPARATION_MARGIN,
  });
});

const isAsapSelected = computed(() => orderType.value === "asap");
const isScheduledSelected = computed(() => orderType.value === "scheduled");

const onConfirm = async () => {
  isSettingMode.value = true;

  const chosenTime = scheduledTime.value;

  try {
    if (basket.orderingMode !== PICKUP) {
      await push("outlet", {
        orderingMode: PICKUP,
        outletSlug: outlet.slug,
      });
    }

    if (!basket.isLoaded) {
      await basket.loadBasket(
        business.id,
        business.currency,
        basket.outletId,
        PICKUP,
        business.salesChannel,
        chosenTime,
      );
    }

    if (business.isCatering) {
      await basket.setMeta({
        eventName: eventName.value ?? null,
        pax: typeof pax.value === "number" ? pax.value : null,
      });
    }

    const { validateScheduleTime } = useMinimumLeadTime();

    const itemSchedules = itemSchedulesStore.schedulesByOutlet[basket.outletId];

    const rescheduleValidation = validateScheduleTime(
      basket.items,
      chosenTime,
      (itemName) =>
        t("components.pickup_ordering_details.reschedule_item_error", {
          itemName,
        }),
      itemSchedules,
    );

    if (!rescheduleValidation.isValid) {
      scheduleErrorMessage.value = rescheduleValidation.errors;
      return;
    }

    await basket.setScheduledTime(chosenTime);

    emits("update");
  } catch (e) {
    report(e);
    scheduleErrorMessage.value = t(
      "components.pickup_ordering_details.error_confirm_failed",
    );
  } finally {
    isSettingMode.value = false;
  }
};

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduleErrorMessage.value = undefined;
  scheduledTime.value = value;
};

const eventName = ref(basket.eventName);
const pax = ref(basket.pax);

const displayAsapRadioOption = computed(() => {
  return (
    orderingModeSettings.value.acceptsAsapOrders &&
    orderingModeSettings.value.outletState === "open"
  );
});

const displayScheduledRadioOption = computed(() => {
  return orderingModeSettings.value.acceptsPreOrders;
});

const isNotAcceptingOrders = computed(() => {
  return !displayAsapRadioOption.value && !displayScheduledRadioOption.value;
});

const isButtonDisabled = computed(() => {
  if (
    displayScheduledRadioOption.value &&
    isScheduledSelected.value &&
    !scheduledTime.value
  ) {
    return true;
  }

  return (
    orderingModeSettings.value.outletState === "closed" ||
    isNotAcceptingOrders.value
  );
});
</script>

<template>
  <div :class="css('')">
    <div :class="css('mode-config')">
      <AtomText type="subtitle">
        {{ t("components.pickup_ordering_details.title") }}
      </AtomText>
      <WoLocation
        :class="css('outlet-card')"
        :address="outlet.address.location"
        :state="mapState[orderingModeSettings.outletState]"
        :state-label="outletStateMap[orderingModeSettings.outletState]"
        :name="outlet.name"
        :state-description="cardDescription"
        :is-active="true"
      />
      <NuxtLink
        :to="
          resolvePath('order', {
            orderingMode: PICKUP,
          })
        "
      >
        <AtomText type="link" color="color-primary">
          {{ t("components.pickup_ordering_details.change_outlet_cta") }}
        </AtomText>
      </NuxtLink>
    </div>
    <div v-if="business.isCatering" :class="css('meta-fields')">
      <AtomText type="subtitle">
        {{ t("components.pickup_ordering_details.details") }}
      </AtomText>
      <BasketEventName @update="(value) => (eventName = value)" />
      <BasketPax @update="(value) => (pax = value)" />
    </div>
    <div
      v-if="orderingModeSettings.outletState !== 'closed'"
      :class="css('scheduler')"
    >
      <SatFormControl :error-message="scheduleErrorMessage">
        <AtomText type="subtitle">
          {{ t("components.pickup_ordering_details.schedule_title") }}
        </AtomText>
        <AtomRadio
          v-if="displayAsapRadioOption"
          name="pickupScheduleOrder"
          :checked="isAsapSelected"
          value="asap"
          @change="
            () => {
              scheduleErrorMessage = undefined;
              orderType = 'asap';
            }
          "
        >
          {{
            t("components.pickup_ordering_details.asap_radio", {
              from: orderingModeSettings.preparationMinutes,
              to:
                orderingModeSettings.preparationMinutes +
                ETA_PREPARATION_MARGIN,
            })
          }}
        </AtomRadio>
        <AtomRadio
          v-if="displayScheduledRadioOption"
          name="pickupScheduleOrder"
          :checked="isScheduledSelected"
          value="scheduled"
          @change="
            () => {
              scheduleErrorMessage = undefined;
              orderType = 'scheduled';
            }
          "
        >
          {{ t("components.pickup_ordering_details.schedule_radio") }}
        </AtomRadio>
        <SchedulePicker
          v-if="
            orderingModeSettings.acceptsPreOrders &&
            isScheduledSelected &&
            selectedOutletId
          "
          :is-root="true"
          :ordering-mode="PICKUP"
          :show-label="false"
          :outlet-id="selectedOutletId"
          :sales-channel="business.salesChannel"
          picker-align="row"
          :value="scheduledTime"
          @select="onSetSchedule"
          @vue:unmounted="() => onSetSchedule()"
        />
        <AtomText v-if="isNotAcceptingOrders" color="color-text-secondary">
          {{ t("components.pickup_ordering_details.no_schedule_available") }}
        </AtomText>
      </SatFormControl>
    </div>
    <div :class="css('footer')">
      <AtomButton
        size="large"
        :is-loading="isSettingMode"
        wide
        :is-disabled="isButtonDisabled"
        @click="onConfirm"
      >
        {{ t("components.pickup_ordering_details.main_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pickup-ordering-details {
  min-width: 320px;

  &__mode-config {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__meta-fields {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  }

  &__outlet-card {
    cursor: default;
  }

  &__scheduler {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__footer {
    margin: var(--spacing-lg) var(--spacing-md);
  }
}
</style>
